import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Spinner, Toast, ToastHeader, ToastBody } from 'reactstrap';
import { useTimeout, useUnmount } from 'usehooks-ts';
import Page from './Page';
import PageNotFound from './PageNotFound';
import { useIsPreview } from '../../shared/previewHelper';
import usePreviewReceiver from '../../shared/usePreviewReceiver';

/**
 * LivePreviewPage receives real-time draft page updates from the Bigfoot Dashboard
 * via window.postMessage and renders the page with full frontend fidelity.
 */
const LivePreviewPage = ({ onPageDataChange, pageNotFoundCallback }) => {
    const isPreview = useIsPreview();

    const [pageData, setPageData] = useState(null);
    const [linkNotice, setLinkNotice] = useState(null);

    const handlers = useMemo(
        () => ({
            BIGFOOT_PREVIEW_UPDATE: (payload) => {
                setPageData((prevData) => {
                    if (JSON.stringify(prevData) === JSON.stringify(payload)) {
                        return prevData;
                    }
                    onPageDataChange?.(payload);
                    return payload;
                });
            },
        }),
        [onPageDataChange]
    );

    const { isConnected } = usePreviewReceiver({
        enabled: isPreview,
        handlers,
    });

    useTimeout(
        () => {
            setLinkNotice(null);
        },
        linkNotice ? 4000 : null
    );

    useUnmount(() => {
        onPageDataChange?.(null);
    });

    useEffect(() => {
        if (isPreview && pageData?.title) {
            document.title = `Live-Vorschau: ${pageData.title}`;
        }
    }, [isPreview, pageData?.title]);

    // Intercept clicks on links to prevent accidentally navigating away in preview mode
    const handleContainerClick = (event) => {
        const anchor = event.target.closest('a');
        if (anchor) {
            event.preventDefault();
            const href = anchor.getAttribute('href') || '';
            setLinkNotice(
                href
                    ? `Navigation zu "${href}" ist in der Live-Vorschau deaktiviert.`
                    : 'Link-Navigation ist in der Live-Vorschau deaktiviert.'
            );
        }
    };

    if (!isPreview) {
        return <PageNotFound pageNotFoundCallback={pageNotFoundCallback} />;
    }

    if (!isConnected && !pageData) {
        return (
            <Container className="py-5 text-center">
                <Row className="justify-content-center">
                    <Col md={8} lg={6}>
                        <div className="p-4 border rounded bg-light shadow-sm">
                            <Spinner color="primary" className="mb-3" />
                            <h2 className="h5 text-primary mb-2">Live-Vorschau wird initialisiert</h2>
                            <p className="text-muted small mb-0">Warte auf Seitendaten aus dem Dashboard...</p>
                        </div>
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
        <div onClick={handleContainerClick} className="position-relative w-100">
            {linkNotice && (
                <div className="toast-container position-fixed bottom-0 end-0 p-3" style={{ zIndex: 1100 }}>
                    <Toast isOpen={Boolean(linkNotice)} fade className="shadow">
                        <ToastHeader toggle={() => setLinkNotice(null)} icon="primary">
                            Live-Vorschau
                        </ToastHeader>
                        <ToastBody className="small text-muted">{linkNotice}</ToastBody>
                    </Toast>
                </div>
            )}
            <Row>
                <Page
                    pageTitle={pageData?.title || 'Vorschau'}
                    showTitle={pageData?.showTitle !== false}
                    pageBody={pageData?.body ?? []}
                    survey={pageData?.survey}
                />
            </Row>
        </div>
    );
};

export default LivePreviewPage;
